import type { FieldValues, Resolver } from 'react-hook-form';
import type { z } from 'zod';

// React Hook Form preserves empty strings for register'd text inputs and
// NaN for register'd number inputs with valueAsNumber when the underlying
// value is absent. Zod 4 schemas with format validators (uuid/url/iso.date/
// regex) reject "" with "Invalid X" instead of treating it as absent, and
// z.number() rejects NaN — in both cases handleSubmit silently aborts.
//
// Normalize top-level "" and NaN → undefined before validation so optional
// fields behave intuitively. Nested objects/arrays are left alone — they
// are typically pre-shaped by setValue and don't suffer this issue.
export function zodResolver<T extends FieldValues>(schema: z.ZodType<T>): Resolver<T> {
  return async (values) => {
    const normalized: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values as Record<string, unknown>)) {
      if (v === '' || (typeof v === 'number' && Number.isNaN(v))) {
        normalized[k] = undefined;
      } else {
        normalized[k] = v;
      }
    }
    const result = await schema.safeParseAsync(normalized);
    if (result.success) {
      return { values: result.data as T, errors: {} };
    }
    const fieldErrors: Record<string, { type: string; message: string }> = {};
    let rootIdx = 0;
    for (const issue of result.error.issues) {
      // RHF без подпути в 'root' submit не блокирует — schema-level
      // ошибки (refine/superRefine с path=[]) кладём в root.<idx>.
      const path = issue.path.length ? issue.path.map(String).join('.') : `root.${rootIdx++}`;
      if (!fieldErrors[path]) {
        fieldErrors[path] = { type: issue.code ?? 'validation', message: issue.message };
      }
    }
    return { values: {}, errors: fieldErrors as never };
  };
}
